// Game State Context
// Manages core game state: player, connection, Phaser instance

import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode, useRef } from 'react';
import { Helix2000Character, JobClass } from '../../../shared/CharacterTypes';
import { EventBus, EVENTS } from '../../../shared/EventBus';
import type { LocalEquipmentVisualsPayload } from '../../network/ClientMessageTypes';
import { gameLogger } from '../../../utils/GameLogger';
import { GameScene } from '../../scenes/GameScene';
import { CharacterService, DBCharacter } from '../../services/CharacterService';
import { pageLifetimeContext } from './pageLifetimeContext';
import { useColyseusMessages } from '../hooks/useColyseusMessages';

interface GameContextState {
  // Auth
  authToken: string | null;
  isAuthenticated: boolean;

  // Player
  player: Helix2000Character | null;
  otherPlayers: Map<string, Helix2000Character>;

  // Game instances
  gameInstance: any | null;
  gameWorld: GameScene | null;
  
  // Connection
  isConnected: boolean;
}

interface GameContextActions {
  setAuthToken: (token: string | null) => void;
  setIsAuthenticated: (authenticated: boolean) => void;
  // SSOT for the self-player (keystone 2026-06-05): useAuth drives this so the
  // unified window engine's WindowHost-mounted windows can read useGame().player.
  // Typed as the raw useState dispatcher so functional updaters
  // (`setPlayer(prev => ...)` in ChatCommandProcessor / useDevActions) type-check.
  setPlayer: React.Dispatch<React.SetStateAction<Helix2000Character | null>>;
  updatePlayer: (updates: Partial<Helix2000Character>) => void;
  setOtherPlayers: (players: Map<string, Helix2000Character>) => void;
  addOtherPlayer: (player: Helix2000Character) => void;
  removeOtherPlayer: (playerId: string) => void;
  setGameInstance: (game: any | null) => void;
  setGameWorld: (world: GameScene | null) => void;
  setIsConnected: (connected: boolean) => void;
  
  // Convenience
  handleStartGame: (token: string, characterData: DBCharacter) => void;
}

type GameContextType = GameContextState & GameContextActions;

// Preserve the Context identity across Vite HMR frames. Providers and
// consumers are updated through different module branches; without this
// page-lifetime registry a transient split can make a valid useGame() call
// appear to be outside GameProvider and leave an error-boundary subtree dead.
const GameContext = pageLifetimeContext(
  'game',
  () => createContext<GameContextType | null>(null),
);

export const useGame = (): GameContextType => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};

/** `useGame()` for leaf components that also render outside a provider (tests, previews). */
export const useOptionalGame = (): GameContextType | null => useContext(GameContext);

interface GameProviderProps {
  children: ReactNode;
}

export const GameProvider: React.FC<GameProviderProps> = ({ children }) => {
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [player, setPlayer] = useState<Helix2000Character | null>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).__gamePlayer = player;
    }
  }, [player]);
  const [otherPlayers, setOtherPlayers] = useState<Map<string, Helix2000Character>>(new Map());
  const [gameInstance, setGameInstance] = useState<any | null>(null);
  const [gameWorld, setGameWorld] = useState<GameScene | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  const characterService = useRef(CharacterService.getInstance());

  const updatePlayer = useCallback((updates: Partial<Helix2000Character>) => {
    setPlayer(prev => prev ? { ...prev, ...updates } : null);
  }, []);

  // The server can acknowledge advancement after the Jobs window closes.
  // Own this subscription with the player state, not the transient window.
  useColyseusMessages({
    jobAdvanceSuccess: (msg: { job?: unknown } | null) => {
      if (typeof msg?.job === 'string' && Object.values(JobClass).includes(msg.job as JobClass)) {
        updatePlayer({ job: msg.job as JobClass });
      }
    },
  });

  // Listen to authoritative equipment visuals from NetworkSyncSystem
  // so the player state in UI windows (Character portrait, etc.) stays in sync.
  useEffect(() => {
    return EventBus.getInstance().on(
      EVENTS.NET_LOCAL_EQUIPMENT_VISUALS,
      (data: LocalEquipmentVisualsPayload) => {
        if (data?.equipmentVisuals) {
          updatePlayer({ equipmentVisuals: data.equipmentVisuals });
        }
      },
    );
  }, [updatePlayer]);

  const addOtherPlayer = useCallback((newPlayer: Helix2000Character) => {
    setOtherPlayers(prev => {
      const next = new Map(prev);
      next.set(newPlayer.id, newPlayer);
      return next;
    });
  }, []);

  const removeOtherPlayer = useCallback((playerId: string) => {
    setOtherPlayers(prev => {
      const next = new Map(prev);
      next.delete(playerId);
      return next;
    });
  }, []);

  const handleStartGame = useCallback((token: string, characterData: DBCharacter) => {
    try {
      setAuthToken(token);
      const gameChar = characterService.current.convertDBToGameCharacter(characterData);
      setPlayer(gameChar);
      setIsAuthenticated(true);
    } catch (error) {
      gameLogger.error('context', '❌ Failed to start game:', error);
    }
  }, []);

  const value: GameContextType = {
    // State
    authToken,
    isAuthenticated,
    player,
    otherPlayers,
    gameInstance,
    gameWorld,
    isConnected,

    // Actions
    setAuthToken,
    setIsAuthenticated,
    setPlayer,
    updatePlayer,
    setOtherPlayers,
    addOtherPlayer,
    removeOtherPlayer,
    setGameInstance,
    setGameWorld,
    setIsConnected,
    handleStartGame,
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
};
